import { useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import { appDatePickerSlots } from "../../shared/datePickerSlots";
import { useDateRangePickerOpenState } from "../../shared/useDateRangePickerOpenState";

/** Guards against implausible values produced by manual keyboard entry. */
const isReasonableDate = (value: Dayjs | null): value is Dayjs =>
  !!value && value.isValid() && value.year() >= 1900 && value.year() <= 2100;

export type FilterBarDateRangePopoverProps = Readonly<{
  open: boolean;
  anchorEl: HTMLElement | null;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onClose: () => void;
  onDateRangeChange: (from: Dayjs | null, to: Dayjs | null) => void;
}>;

export function FilterBarDateRangePopover({
  open,
  anchorEl,
  fromDate,
  toDate,
  onClose,
  onDateRangeChange,
}: FilterBarDateRangePopoverProps) {
  const {
    resetPicker,
    getPickerOpenProps,
    getTextFieldOpenOnClick,
    getOpenPickerButtonOnClick,
  } = useDateRangePickerOpenState();

  useEffect(() => {
    if (!open) {
      resetPicker();
    }
  }, [open, resetPicker]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{ paper: { sx: { borderRadius: "3px", width: 300 } } }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#172B4D" }}>
            Select Date Range
          </Typography>
          <DatePicker
            label="From Date"
            value={fromDate}
            format="DD-MMM-YYYY"
            slots={appDatePickerSlots}
            {...getPickerOpenProps("from")}
            onChange={(newValue) => {
              if (!isReasonableDate(newValue)) return;
              onDateRangeChange(
                newValue,
                isReasonableDate(toDate) ? toDate : null,
              );
            }}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                readOnly: true,
                ...getTextFieldOpenOnClick("from"),
              },
              openPickerButton: getOpenPickerButtonOnClick("from"),
            }}
            maxDate={toDate ?? dayjs()}
          />
          <DatePicker
            label="To Date"
            value={toDate}
            format="DD-MMM-YYYY"
            slots={appDatePickerSlots}
            {...getPickerOpenProps("to")}
            onChange={(newValue) => {
              if (!isReasonableDate(newValue)) return;
              onDateRangeChange(
                isReasonableDate(fromDate) ? fromDate : null,
                newValue,
              );
            }}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                readOnly: true,
                ...getTextFieldOpenOnClick("to"),
              },
              openPickerButton: getOpenPickerButtonOnClick("to"),
            }}
            minDate={fromDate ?? undefined}
            maxDate={dayjs()}
          />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              variant="text"
              size="small"
              sx={{ borderRadius: "3px", color: "#295EF1" }}
              onClick={() => onDateRangeChange(null, null)}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              sx={{ borderRadius: "3px", boxShadow: "none" }}
              onClick={onClose}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>
    </Popover>
  );
}
